// Guards for APIs our es2022 TS lib allows but old engines lack (#63684): the same
// file next injects into every Next.js page, pinned to next's version in the catalog.
import '@next/polyfill-module';

// The natives these stand in for are non-enumerable, so plain assignment would leak
// them into `for...in` over an array or string.
const defineGuard = (target, name, value) =>
   Object.defineProperty(target, name, { value, writable: true, configurable: true });

// Not in Next's list. ES2023, missing below Chrome 97 / Safari 15.4, and dependencies
// aren't transpiled: tiptap's findLast crashes those engines today (IFIXIT-JS-Q7D).
if (!Array.prototype.findLast) {
   defineGuard(Array.prototype, 'findLast', function findLast(callback, thisArg) {
      for (let i = this.length - 1; i >= 0; i--) {
         if (callback.call(thisArg, this[i], i, this)) {
            return this[i];
         }
      }
   });
   defineGuard(Array.prototype, 'findLastIndex', function findLastIndex(callback, thisArg) {
      for (let i = this.length - 1; i >= 0; i--) {
         if (callback.call(thisArg, this[i], i, this)) {
            return i;
         }
      }
      return -1;
   });
}

// Next guards the Array twin but not this one, and they ship in the same engines
// (Chrome 92 / Safari 15.4), so reaching for it is #63445 all over again.
if (!String.prototype.at) {
   defineGuard(String.prototype, 'at', function at(index) {
      const string = String(this);
      const truncated = Math.trunc(index) || 0;
      const offset = truncated < 0 ? truncated + string.length : truncated;
      return offset < 0 || offset >= string.length ? undefined : string[offset];
   });
}

// Missing below Safari 27. pdf.js 6 reads page text with `for await` over a
// ReadableStream, so without it the PDF viewer's search finds nothing on iOS 26.
if (typeof ReadableStream !== 'undefined' && !ReadableStream.prototype[Symbol.asyncIterator]) {
   defineGuard(ReadableStream.prototype, Symbol.asyncIterator, async function* values() {
      const reader = this.getReader();
      let isDone = false;
      try {
         while (true) {
            // eslint-disable-next-line no-await-in-loop -- a stream yields one chunk per read
            const { done, value } = await reader.read();
            if (done) {
               isDone = true;
               return;
            }
            yield value;
         }
      } finally {
         // Leaving the loop early cancels the stream, as the native iterator does.
         if (!isDone) {
            try {
               await reader.cancel();
            } catch {
               // An errored stream has already thrown its own error.
            }
         }
         reader.releaseLock();
      }
   });
}

// Missing below Chrome 85 / Safari 14. Promise.any rejects with an AggregateError,
// so neither is usable without the other.
if (typeof AggregateError === 'undefined') {
   window.AggregateError = class AggregateError extends Error {
      constructor(errors, message) {
         super(message);
         this.name = 'AggregateError';
         this.errors = [...errors];
      }
   };
}

if (!Promise.any) {
   Promise.any = function any(values) {
      const promises = [...values];
      return new Promise((resolve, reject) => {
         const errors = [];
         let pending = promises.length;
         const rejectAll = () => reject(new AggregateError(errors, 'All promises were rejected'));

         if (pending === 0) {
            rejectAll();
            return;
         }
         promises.forEach((value, index) => {
            Promise.resolve(value).then(resolve, error => {
               errors[index] = error;
               pending -= 1;
               if (pending === 0) {
                  rejectAll();
               }
            });
         });
      });
   };
}
